import { createFileRoute } from "@tanstack/react-router";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { Mail, MessageSquare, Twitter, MapPin } from "lucide-react";
import { generateBreadcrumbSchema, generateWebPageSchema } from "@/lib/utils";

const SITE_URL = "https://letslistbusiness.com";

export const Route = createFileRoute("/contact/")({
  head: () => {
    const title = "Contact LetsListBusiness — Support, Feedback, Press";
    const description = "Get in touch with the LetsListBusiness team for support, feedback, partnerships and press inquiries.";
    const url = SITE_URL + "/contact/";
    
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: "Contact LetsListBusiness" },
        { property: "og:description", content: "Reach the LetsListBusiness team." },
      ],
      links: [{ rel: "canonical", href: url }],
      schema: [
        generateWebPageSchema(title, description, url),
        generateBreadcrumbSchema([
          { label: "Home", href: "/" },
          { label: "Contact", href: "/contact/" },
        ], SITE_URL),
      ],
    };
  },
  component: ContactPage,
});

function ContactPage() {
  return (
    <>
      <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: "Contact" }]} />

      <section className="container mx-auto px-4 pb-16">
        <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-card-lg">
          <div className="grid lg:grid-cols-5">
            {/* Left — dark info panel */}
            <div className="bg-secondary p-8 text-secondary-foreground sm:p-12 lg:col-span-2">
              <div className="text-xs font-semibold uppercase tracking-[0.3em] text-accent">Get in touch</div>
              <h1 className="mt-4 font-display text-4xl font-bold leading-tight sm:text-5xl">
                We read every <em className="not-italic text-accent">message.</em>
              </h1>
              <p className="mt-5 text-secondary-foreground/80">
                For listing issues, press inquiries, or just to say hi — drop
                us a line. We typically respond within one business day.
              </p>

              <div className="mt-10 space-y-5">
                <Channel icon={Mail} label="General" value="hello@letslistbusiness.com" />
                <Channel icon={MessageSquare} label="Press" value="press@letslistbusiness.com" />
                <Channel icon={Twitter} label="Social" value="@localix" />
                <Channel icon={MapPin} label="HQ" value="Brooklyn, NY" />
              </div>
            </div>

            {/* Right — form */}
            <form className="space-y-5 p-8 sm:p-12 lg:col-span-3">
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Send us a note</div>
                <h2 className="mt-2 font-display text-2xl font-bold">How can we help?</h2>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Name"><input className="field" placeholder="Your name" /></Field>
                <Field label="Email"><input className="field" type="email" placeholder="you@example.com" /></Field>
              </div>
              <Field label="Subject"><input className="field" placeholder="What's this about?" /></Field>
              <Field label="Message"><textarea className="field min-h-[160px]" placeholder="Tell us more…" /></Field>
              <button type="button" className="rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground hover:opacity-95">
                Send message
              </button>
            </form>
          </div>
        </div>
      </section>
      <style>{`.field{width:100%;border-radius:.625rem;border:1px solid var(--border);background:var(--background);padding:.7rem .9rem;font-size:.875rem;outline:none;transition:all .15s}.field:focus{border-color:var(--ring);box-shadow:0 0 0 3px color-mix(in oklab, var(--ring) 25%, transparent)}`}</style>
    </>
  );
}

function Channel({ icon: Icon, label, value }: { icon: React.ComponentType<{ className?: string }>; label: string; value: string }) {
  return (
    <div className="flex items-center gap-4 border-b border-secondary-foreground/15 pb-4">
      <Icon className="h-5 w-5 text-accent" />
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-secondary-foreground/60">{label}</div>
        <div className="mt-0.5 text-sm font-medium">{value}</div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
